import { Link } from "react-router-dom";
import { ArrowUpRight, ArrowRight } from "lucide-react";
import { motion } from "framer-motion";
import Reveal from "@/components/Reveal";

export function Container({ children, className = "" }) {
  return (
    <div className={`mx-auto max-w-7xl px-6 md:px-12 lg:px-24 ${className}`}>
      {children}
    </div>
  );
}

export function SectionLabel({ children, className = "" }) {
  return (
    <div className={`flex items-center gap-3 ${className}`}>
      <span className="h-px w-8 bg-accent" />
      <span className="overline">{children}</span>
    </div>
  );
}

export function PrimaryButton({ to, href, children, testId, ...props }) {
  const cls =
    "group inline-flex items-center justify-center gap-2 bg-accent px-7 py-3.5 font-mono text-xs uppercase tracking-[0.15em] text-white transition-colors hover:bg-accent-hover";
  const inner = (
    <>
      {children}
      <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
    </>
  );
  if (href) {
    return (
      <a href={href} className={cls} data-testid={testId} {...props}>
        {inner}
      </a>
    );
  }
  return (
    <Link to={to} className={cls} data-testid={testId} {...props}>
      {inner}
    </Link>
  );
}

export function SecondaryButton({ to, href, children, testId, ...props }) {
  const cls =
    "group inline-flex items-center justify-center gap-2 border border-white/15 px-7 py-3.5 font-mono text-xs uppercase tracking-[0.15em] text-primary-ink transition-colors hover:border-white/40 hover:bg-white/5";
  const inner = (
    <>
      {children}
      <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
    </>
  );
  if (href) {
    return (
      <a
        href={href}
        target={href.startsWith("http") ? "_blank" : undefined}
        rel="noopener noreferrer"
        className={cls}
        data-testid={testId}
        {...props}
      >
        {inner}
      </a>
    );
  }
  return (
    <Link to={to} className={cls} data-testid={testId} {...props}>
      {inner}
    </Link>
  );
}

export function CTASection() {
  return (
    <section className="hairline-t relative overflow-hidden" data-testid="cta-section">
      <div className="pointer-events-none absolute left-1/2 top-0 h-[420px] w-[720px] -translate-x-1/2 rounded-full bg-accent/10 blur-[140px]" />
      <Container className="relative py-28 text-center md:py-40">
        <Reveal>
          <p className="overline mb-6">Let's build</p>
          <h2 className="mx-auto max-w-3xl font-cabinet text-4xl font-medium leading-[1.05] tracking-tight text-primary-ink md:text-6xl">
            Have an idea you'd like to build?
          </h2>
          <p className="mt-6 font-cabinet text-4xl font-medium tracking-tight text-muted-ink md:text-6xl">
            Let's talk.
          </p>
        </Reveal>
        <Reveal delay={0.15} className="mt-12 flex flex-wrap justify-center gap-4">
          <PrimaryButton to="/contact" testId="cta-work-with-me">
            Work With Me
          </PrimaryButton>
          <SecondaryButton to="/projects" testId="cta-view-work">
            View My Work
          </SecondaryButton>
        </Reveal>
      </Container>
    </section>
  );
}

export function PageHero({ label, title, children }) {
  return (
    <section className="relative pt-36 md:pt-44">
      <Container>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
        >
          <SectionLabel>{label}</SectionLabel>
          <h1 className="mt-8 max-w-4xl font-cabinet text-4xl font-medium leading-[1.03] tracking-tight text-primary-ink sm:text-5xl lg:text-6xl">
            {title}
          </h1>
          {children && (
            <div className="mt-8 max-w-2xl text-lg leading-relaxed text-secondary-ink">
              {children}
            </div>
          )}
        </motion.div>
      </Container>
    </section>
  );
}
